import type {
  Competence,
  CompetenceSection,
  Item,
  PersonalCompetencesItem,
  SelectedItem
} from '@/generated-api'
import type { IProfileListItem } from '@/src/interfaces/IProfileListItem'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { IDropdownItem } from '@/src/interfaces/components/IDropdownComponent'
import type { SelectItem } from '@/src/interfaces/components/ISelectComponent'
import { useDrawerStore } from '@/stores'
import { profileDropdown } from '@/application/helpers/profileHelper'

export const useProfileCompetencesStore = defineStore('profileCompetencesStore', () => {
  const initialized = ref<boolean>(false)
  const drawerStore = useDrawerStore()
  const professionalCompetences = ref<Competence[]>([])
  const personalCompetences = ref<PersonalCompetencesItem[]>([])
  const languageItems = ref<SelectedItem[]>([])
  const itCompetences = ref<SelectedItem[]>([])
  const selectedDriverLicenses = ref<IProfileListItem[]>([])

  const init = async (competenceSection: CompetenceSection | undefined) => {
    if (competenceSection != undefined) {
      professionalCompetences.value = competenceSection.competences ?? []
      personalCompetences.value = competenceSection.personalCompetences?.items ?? []
      languageItems.value = competenceSection?.languageSkill?.items ?? []
      itCompetences.value = competenceSection.itCompetences?.items ?? []
      //driverLicences.value = competenceSection.driverLicense?.items ?? []

      selectedDriverLicenses.value =
        competenceSection?.driverLicense?.items?.map((driverLicense: Item) => ({
          id: driverLicense.id ?? 0,
          title: driverLicense.text ?? '',
          dropdownItems: [...profileDropdown().slice(3, 4)],
          hide: false
        })) ?? []
    }

    initialized.value = true
  }

  const mapCompetences = computed<IProfileListItem[]>(() => {
    return (
      professionalCompetences.value.map((item: Competence) => ({
        id: item.id ?? 0,
        title: item.text ?? '',
        isPublic: item.selected ?? false,
        hide: false,
        dropdownItems: [
          ...profileDropdown()
            .slice(0, 2)
            .filter((dropdownItem: IDropdownItem) => {
              if (item.selected === true && dropdownItem.icon !== 'eye') {
                return dropdownItem
              } else if (item.selected === false && dropdownItem.icon !== 'eye-slash') {
                return dropdownItem
              } else {
                return
              }
            })
        ]
      })) ?? []
    )
  })

  const mapPersonalCompetences = computed<IProfileListItem[]>(() => {
    return (
      personalCompetences.value?.map((item: PersonalCompetencesItem) => ({
        id: item.id ?? 0,
        title: item.title ?? '',
        description: item.description ?? '',
        isPublic: item.publicState ?? false,
        hide: false,
        dropdownItems: [
          ...profileDropdown().filter((dropdownItem: IDropdownItem) => {
            if (item.publicState === true && dropdownItem.icon !== 'eye') {
              return dropdownItem
            } else if (item.publicState === false && dropdownItem.icon !== 'eye-slash') {
              return dropdownItem
            } else {
              return
            }
          })
        ]
      })) ?? []
    )
  })

  const mapLanguageSkills = computed<IProfileListItem[]>(() => {
    return (
      languageItems.value?.map((item: SelectedItem) => ({
        id: item.id ?? 0,
        title: item.text ?? '',
        description: item.levelText ?? '',
        hide: false,
        dropdownItems: [...profileDropdown().slice(2, 4)]
      })) ?? []
    )
  })

  const mapItCompetences = computed<IProfileListItem[]>(() => {
    return (
      itCompetences.value.map((item: SelectedItem) => ({
        id: item.id ?? 0,
        title: item.text ?? '',
        description: item.levelText ?? '',
        hide: false,
        dropdownItems: [...profileDropdown().slice(2, 4)]
      })) ?? []
    )
  })

  const orderList = (
    ListToOrder: PersonalCompetencesItem[] | SelectedItem[] | Item[],
    items: IProfileListItem[]
  ) => {
    ListToOrder.sort((a, b) => {
      const indexA = items.findIndex((item) => item.id === a.id)
      const indexB = items.findIndex((item) => item.id === b.id)
      return indexA - indexB
    })
  }

  const updateSelectedDriverLicenses = (licenses: SelectItem[]) => {
    selectedDriverLicenses.value = licenses.map((license) => ({
      id: license.id ?? 0,
      title: license.name,
      dropdownItems: [...profileDropdown().slice(3, 4)],
      hide: false
    }))

    drawerStore.closeSubDrawer()
    drawerStore.setCanCloseDrawer(false)
  }

  const setInitialized = (toggle: boolean) => {
    initialized.value = toggle
  }

  return {
    initialized,
    professionalCompetences,
    languageItems,
    personalCompetences,
    itCompetences,
    selectedDriverLicenses,
    mapCompetences,
    mapPersonalCompetences,
    mapLanguageSkills,
    mapItCompetences,
    updateSelectedDriverLicenses,
    init,
    orderList,
    setInitialized
  }
})
